import styled from '@emotion/styled';
import { tokens } from '../../../design-system/tokens';

export const TextAreaContainer = styled.div`
  position: relative;
  width: 100%;
`;

export const StyledTextArea = styled.textarea<{
  $minHeight: string;
  $isListening: boolean;
  $autoGrow: boolean;
}>`
  display: block;
  width: 100%;
  min-height: ${({ $minHeight }) => $minHeight};
  padding: ${tokens.spacing.sm} ${tokens.spacing.md};
  padding-right: ${tokens.spacing['3xl']}; /* Espacio para el botón de voz */
  background-color: ${tokens.colors.inputBackground};
  border: 1px solid ${tokens.colors.inputBackground};
  border-radius: ${tokens.borderRadius.sm};
  color: ${tokens.colors.blue[900]};
  font-size: ${tokens.typography.fontSize.sm};
  font-family: ${tokens.typography.fontFamily.primary};
  font-weight: ${tokens.typography.fontWeight.semibold};
  resize: ${({ $autoGrow }) => ($autoGrow ? 'none' : 'vertical')};
  overflow-y: ${({ $autoGrow }) => ($autoGrow ? 'hidden' : 'auto')};
  transition: ${tokens.transitions.fast};
  box-sizing: border-box;

  /* Efecto visual cuando está escuchando */
  ${({ $isListening }) =>
    $isListening &&
    `
    animation: pulse 2s infinite;
  `}

  &::placeholder {
    color: ${tokens.colors.placeholder};
  }

  &:focus {
    outline: none;
  }

  &:disabled {
    background-color: ${tokens.colors.basics[400]};
    color: ${tokens.colors.basics[1100]};
    cursor: not-allowed;
  }

  @keyframes pulse {
    0% {
      box-shadow: 0 0 0 2px ${tokens.colors.basics[300]};
    }
    50% {
      box-shadow: 0 0 0 2px ${tokens.colors.blue[400]};
    }
    100% {
      box-shadow: 0 0 0 2px ${tokens.colors.basics[300]};
    }
  }
`;

export const VoiceButtonContainer = styled.div`
  position: absolute;
  top: ${tokens.spacing.xs};
  right: ${tokens.spacing.sm};
  z-index: 1;
`;

export const VoiceButton = styled.button<{
  $isListening: boolean;
  $isDisabled: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ $isListening }) =>
    $isListening ? tokens.colors.blue[900] : 'transparent'};
  border: none;
  border-radius: ${tokens.borderRadius.full};
  color: ${tokens.colors.basics[0]};
  cursor: ${({ $isDisabled }) => ($isDisabled ? 'not-allowed' : 'pointer')};
  transition: ${tokens.transitions.fast};
  opacity: ${({ $isDisabled }) => ($isDisabled ? 0.5 : 1)};
  width: 40px;
  height: 40px;
  padding: 0;

  &:focus {
    outline: none;
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }

  /* Animación de pulso cuando está grabando */
  ${({ $isListening }) => $isListening && `animation: voicePulse 1.5s infinite;`}

  @keyframes voicePulse {
    0% {
      transform: scale(1);
      box-shadow: 0 0 0 0 ${tokens.colors.blue[900]};
    }
    70% {
      transform: scale(1.05);
      box-shadow: 0 0 0 10px rgba(239, 68, 68, 0);
    }
    100% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(239, 68, 68, 0);
    }
  }
`;

export const CharacterCounter = styled.div<{ $isAtLimit: boolean }>`
  position: absolute;
  bottom: ${tokens.spacing.xs};
  right: ${tokens.spacing.sm};
  font-size: ${tokens.typography.fontSize.xs};
  color: ${tokens.colors.basics[0]};
  font-family: ${tokens.typography.fontFamily.primary};
  pointer-events: none;
  font-weight: ${tokens.typography.fontWeight.medium};
  background-color: ${({ $isAtLimit }) =>
    $isAtLimit ? 'rgba(239, 68, 68, 0.1)' : 'transparent'};
  border-radius: ${tokens.borderRadius.sm};
  transition: ${tokens.transitions.fast};
`;

export const TextAreaFooter = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${tokens.spacing.sm};
  margin-top: ${tokens.spacing.xs};
`;

export const ExternalCharacterCounter = styled.div`
  flex: 0 0 auto;
  margin-left: auto;
  font-size: ${tokens.typography.fontSize.xs};
  font-family: ${tokens.typography.fontFamily.primary};
  font-weight: ${tokens.typography.fontWeight.medium};
  transition: ${tokens.transitions.fast};
`;
